/**
 * Listado global de accesos de dispositivo (iLO/IPMI, SSH, RDP, web…).
 *
 * La ficha de cada CI tiene su pestaña «Accesos», pero desde ahí solo se ve el
 * elemento que tienes abierto. Cuando alguien crea accesos en varios equipos
 * (o no recuerda en cuál) no había forma de verlos todos: esta pantalla los
 * lista con su equipo, su tipo y su destino.
 *
 * El backend resuelve el nombre del CI con `?with_object=true`; si el equipo
 * ya no existe el acceso se marca como huérfano en vez de ocultarse.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LinkIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import ListPage from './common/ListPage';

const OBJECT_LABELS = {
  rack_element: 'Servidor / switch / cabina',
  virt_vm: 'Máquina virtual',
  virt_hypervisor: 'Hipervisor',
  firewall: 'Firewall',
  router: 'Router',
  wifi_controller: 'Controladora WiFi',
  wifi_access_point: 'Punto de acceso WiFi',
  workstation: 'Estación de trabajo',
  printer: 'Impresora',
  ip_phone: 'Teléfono IP',
  ups: 'SAI',
  peripheral: 'Periférico',
};

const dash = '—';

export default function AccessPointsList({ companyId }) {
  const [searchParams] = useSearchParams();
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [filterType, setFilterType] = useState('');

  const load = useCallback(async () => {
    if (!companyId) { setPoints([]); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await api.getCiAccessPointsAll(companyId);
      setPoints(res.access_points || []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los accesos');
      setPoints([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return points.filter((p) => {
      if (filterType && p.access_type !== filterType) return false;
      if (!q) return true;
      return [
        p.name,
        p.host,
        p.via,
        p.object_name,
        OBJECT_LABELS[p.object_type],
        p.access_type_label,
        p.notes,
      ].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [points, search, filterType]);

  const typeOptions = useMemo(() => {
    const map = new Map();
    points.forEach((p) => {
      if (p.access_type) map.set(p.access_type, p.access_type_label || p.access_type);
    });
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [points]);

  const columns = [
    {
      key: 'name',
      label: 'Acceso',
      render: (p) => (
        <span style={{ fontWeight: 600 }}>
          {p.name}
          {p.is_primary ? <span title="Acceso principal" style={{ marginLeft: 6, color: 'var(--accent)' }}>★</span> : null}
        </span>
      ),
    },
    { key: 'access_type_label', label: 'Tipo', render: (p) => p.access_type_label || p.access_type || dash },
    {
      key: 'object_name',
      label: 'Equipo',
      render: (p) => (p.object_exists
        ? `${p.object_name} · ${OBJECT_LABELS[p.object_type] || p.object_type}`
        : `⚠ Huérfano (#${p.object_id}) · ${OBJECT_LABELS[p.object_type] || p.object_type}`),
      sortValue: (p) => p.object_name || '',
    },
    {
      key: 'host',
      label: 'Destino',
      render: (p) => (p.host ? `${p.host}${p.port ? `:${p.port}` : ''}` : dash),
    },
    { key: 'via', label: 'Cómo se llega', render: (p) => p.via || dash },
    { key: 'notes', label: 'Notas', render: (p) => p.notes || dash },
  ];

  const exportRows = useMemo(() => filtered.map((p) => ({
    Acceso: p.name,
    Tipo: p.access_type_label || p.access_type,
    Equipo: p.object_exists ? p.object_name : `Huérfano (#${p.object_id})`,
    'Tipo de equipo': OBJECT_LABELS[p.object_type] || p.object_type,
    Destino: p.host ? `${p.host}${p.port ? `:${p.port}` : ''}` : '',
    'Cómo se llega': p.via || '',
    Principal: p.is_primary ? 'Sí' : 'No',
    Notas: p.notes || '',
  })), [filtered]);

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">Selecciona una empresa para ver los accesos.</div>
      </div>
    );
  }

  return (
    <ListPage
      icon={ShieldCheckIcon}
      title="Accesos de dispositivo"
      subtitle={`${filtered.length} de ${points.length} accesos · iLO/IPMI, SSH, RDP, web de gestión…`}
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder="Buscar por acceso, equipo, host, nota…"
      filters={[{
        key: 'type',
        label: 'Tipo de acceso',
        allLabel: 'Cualquier tipo',
        value: filterType,
        onChange: setFilterType,
        options: typeOptions,
      }]}
      exportRows={exportRows}
      exportFileBase="accesos-dispositivo"
      exportSheetName="Accesos"
      columns={columns}
      columnStorageKey="cmdb_cols_access_points"
      companyId={companyId}
      defaultVisible={['name', 'access_type_label', 'object_name', 'host', 'via']}
      rows={filtered}
      rowKey={(p) => p.id}
      rowId={(p) => `access-row-${p.id}`}
      views={[]}
      loading={loading}
      loadingText="Cargando accesos…"
      error={error}
      empty={{
        icon: <LinkIcon style={{ width: 26, height: 26 }} />,
        title: points.length === 0 ? 'Todavía no hay accesos documentados' : 'Ningún acceso coincide con el filtro',
        subtitle: points.length === 0
          ? 'Se crean desde la pestaña «Accesos» de cada equipo.'
          : 'Prueba a quitar la búsqueda o el filtro de tipo.',
      }}
    />
  );
}
